import * as Pica from 'pica';

export const load = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

export const loadImageOnCanvas = async (src: string): Promise<HTMLCanvasElement> => {
  const image = await load(src);

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      reject();
      return;
    }

    canvas.width = image.width;
    canvas.height = image.height;

    context.drawImage(image, 0, 0);
    resolve(canvas);
  });
};

export const resizeCanvas = (
  image: HTMLCanvasElement,
  width: number,
  height: number,
  keepRatio = false,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw Error('Error while executing resizeCanvas function, context is null');
  }

  canvas.width = width;
  canvas.height = height;

  if (!keepRatio) {
    context.drawImage(image, 0, 0, image.width, image.height, 0, 0, width, height);
  } else {
    const ratio = Math.max(width / image.width, height / image.height);
    width = image.width * ratio;
    height = image.height * ratio;

    context.drawImage(
      image,
      0,
      0,
      image.width,
      image.height,
      (canvas.width - width) / 2,
      (canvas.height - height) / 2,
      width,
      height,
    );
  }
  return canvas;
};

/**
 * Resizes and centers an image onto a new canvas of the specified dimensions.
 * The image will be drawn onto the canvas with its center aligned.
 *
 * @param sourceImage - The source image to be resized and centered.
 * @param targetWidth - The desired width of the new canvas.
 * @param targetHeight - The desired height of the new canvas.
 * @returns A new canvas element containing the resized and centered image.
 * @throws Error if the 2D rendering context cannot be obtained.
 */
export const resizeAndCenterImage = (
  sourceImage: HTMLCanvasElement,
  targetWidth: number,
  targetHeight: number,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Unable to obtain 2D rendering context');
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  // This formula makes sure crop is made in the center of source image.
  const offsetX = (targetWidth - sourceImage.width) / 2;
  const offsetY = (targetHeight - sourceImage.height) / 2;

  context.drawImage(
    sourceImage,
    0,
    0,
    sourceImage.width,
    sourceImage.height,
    offsetX,
    offsetY,
    sourceImage.width,
    sourceImage.height,
  );

  return canvas;
};

export enum ImageFormat {
  PNG = 'image/png',
  JPEG = 'image/jpeg',
  WEBP = 'image/webp',
  JPG = 'image/jpg',
}

export const canvasToBuffer = async (
  canvas: HTMLCanvasElement,
  format = ImageFormat.PNG,
  quality = 1,
): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject();
          return;
        }

        const bufferArray = await blob.arrayBuffer();
        if (!bufferArray) {
          reject();
          return;
        }

        resolve(bufferArray);
      },
      format,
      quality,
    );
  });
};

export const canvasToBase64 = (
  canvas: HTMLCanvasElement,
  format = ImageFormat.PNG,
  quality = 1,
): string => {
  return canvas.toDataURL(format, quality);
};

export const antialiasImageRatioResize = (
  image: HTMLCanvasElement,
  targetWidth: number,
  targetHeight: number,
  d = 0.8,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw 'Context is null!';

  canvas.width = image.width * d;
  canvas.height = image.height * d;

  if (canvas.width < targetWidth && canvas.height < targetHeight) {
    const ratio =
      image.width > image.height ? image.height / image.width : image.width / image.height;
    canvas.width = image.width <= image.height ? targetWidth * ratio : targetWidth;
    canvas.height = image.width <= image.height ? targetHeight : targetHeight * ratio;
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  if (canvas.width > targetWidth || canvas.height > targetHeight)
    return antialiasImageRatioResize(canvas, targetWidth, targetHeight);
  return canvas;
};

export const sharpenCanvas = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  mix: number,
): HTMLCanvasElement => {
  const context = canvas.getContext('2d');
  if (!context) throw 'Context is null!';

  let x;
  let sx;
  let sy;

  let r;
  let g;
  let b;

  let dstOff;
  let srcOff;

  let wt;
  let cx;
  let cy;
  let scy;
  let scx;
  const weights = [0, -1, 0, -1, 5, -1, 0, -1, 0];
  const katet = Math.round(Math.sqrt(weights.length));
  const half = (katet * 0.5) | 0;
  const dstData = context.createImageData(width, height);
  const dstBuff = dstData.data;
  const srcBuff = context.getImageData(0, 0, width, height).data;
  let y = height;

  while (y--) {
    x = width;
    while (x--) {
      sy = y;
      sx = x;
      dstOff = (y * width + x) * 4;
      r = 0;
      g = 0;
      b = 0;

      for (cy = 0; cy < katet; cy++) {
        for (cx = 0; cx < katet; cx++) {
          scy = sy + cy - half;
          scx = sx + cx - half;

          if (scy >= 0 && scy < height && scx >= 0 && scx < width) {
            srcOff = (scy * width + scx) * 4;
            wt = weights[cy * katet + cx];

            r += srcBuff[srcOff] * wt;
            g += srcBuff[srcOff + 1] * wt;
            b += srcBuff[srcOff + 2] * wt;
          }
        }
      }

      dstBuff[dstOff] = r * mix + srcBuff[dstOff] * (1 - mix);
      dstBuff[dstOff + 1] = g * mix + srcBuff[dstOff + 1] * (1 - mix);
      dstBuff[dstOff + 2] = b * mix + srcBuff[dstOff + 2] * (1 - mix);
      dstBuff[dstOff + 3] = srcBuff[dstOff + 3];
    }
  }

  context.putImageData(dstData, 0, 0);
  return canvas;
};

export const picaResize = async (
  image: HTMLCanvasElement | HTMLImageElement,
  width: number,
  height: number,
): Promise<HTMLCanvasElement> => {
  const pica = new Pica.default();

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  return pica.resize(image, canvas, { filter: 'lanczos2' });
};
